/**
 * Custom hook for measurements print functionality
 * Handles print settings, HTML generation, and print window management
 */

import { useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import type {
  PrintSettings,
  Meranie,
  MeasurementSummary,
  ChartDataPoint,
  Klient,
} from "../types";
import { formatDate, formatNumber, getClientDisplayName } from "../utils";

const defaultPrintSettings: PrintSettings = {
  includeCharts: true,
  includeSummary: true,
  columns: {
    date: true,
    weight: true,
    bodyFat: true,
    muscleMass: true,
    visceralFat: true,
    bmr: true,
  },
};

export function useMeasurementsPrint() {
  const { t } = useTranslation();
  const [printSettings, setPrintSettings] =
    useState<PrintSettings>(defaultPrintSettings);

  /**
   * Toggle all columns on/off
   */
  const toggleAllColumns = (checked: boolean) => {
    setPrintSettings(prev => ({
      ...prev,
      columns: {
        date: checked,
        weight: checked,
        bodyFat: checked,
        muscleMass: checked,
        visceralFat: checked,
        bmr: checked,
      },
    }));
  };

  /**
   * Check if all columns are selected
   */
  const areAllColumnsSelected = () => {
    return Object.values(printSettings.columns).every(v => v);
  };

  /**
   * Generate print HTML for a single client
   */
  const generatePrintHTML = (
    klient: Klient,
    merania: Meranie[],
    summary: MeasurementSummary | null,
    chartData: ChartDataPoint[]
  ): string => {
    const clientName = getClientDisplayName(klient);

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${t("measurements:print.title")} - ${clientName}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 10px;
            color: #333;
          }
          h2 {
            font-size: 18px;
            margin-top: 30px;
            margin-bottom: 15px;
            color: #555;
            border-bottom: 2px solid #ddd;
            padding-bottom: 5px;
          }
          .client-info {
            margin-bottom: 20px;
            font-size: 14px;
            color: #666;
          }
          .summary-cards {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 30px;
          }
          .card {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 15px;
            background: #f9f9f9;
          }
          .card-title {
            font-size: 12px;
            color: #666;
            margin-bottom: 8px;
            text-transform: uppercase;
          }
          .card-value {
            font-size: 24px;
            font-weight: bold;
            color: #333;
            margin-bottom: 4px;
          }
          .card-subtitle {
            font-size: 11px;
            color: #999;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            padding: 10px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
            font-size: 12px;
            text-transform: uppercase;
            color: #666;
          }
          td {
            font-size: 14px;
          }
          .text-right {
            text-align: right;
          }
          .chart-placeholder {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 40px;
            text-align: center;
            background: #f9f9f9;
            color: #999;
            margin-bottom: 30px;
          }
          @media print {
            body {
              padding: 10px;
            }
            .summary-cards {
              page-break-inside: avoid;
            }
            table {
              page-break-inside: auto;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
          }
        </style>
      </head>
      <body>
        <h1>${t("measurements:print.title")}</h1>
        <div class="client-info">
          <strong>${t("measurements:print.client")}:</strong> ${clientName}
        </div>
    `;

    // Include summary if enabled
    if (printSettings.includeSummary && summary) {
      html += `
        <h2>${t("measurements:print.latestMeasurements")}</h2>
        <div class="summary-cards">
          <div class="card">
            <div class="card-title">${t("measurements:cards.weight")}</div>
            <div class="card-value">${summary.weight || "-"} kg</div>
            <div class="card-subtitle">${formatDate(summary.date)}</div>
          </div>
          <div class="card">
            <div class="card-title">${t("measurements:cards.bodyFat")}</div>
            <div class="card-value">${summary.bodyFat || "-"}%</div>
            <div class="card-subtitle">${formatNumber(summary.fatMassKg)} kg</div>
          </div>
          <div class="card">
            <div class="card-title">${t("measurements:cards.muscleMass")}</div>
            <div class="card-value">${summary.muscleMass || "-"}%</div>
            <div class="card-subtitle">${formatNumber(summary.muscleMassKg)} kg</div>
          </div>
          <div class="card">
            <div class="card-title">${t("measurements:cards.visceralFat")}</div>
            <div class="card-value">${summary.visceralFat || "-"}</div>
            <div class="card-subtitle">${t("measurements:cards.level")}</div>
          </div>
        </div>
      `;
    }

    // Include charts placeholder if enabled
    if (printSettings.includeCharts && chartData.length > 0) {
      html += `
        <h2>${t("measurements:progressTrends")}</h2>
        <div class="chart-placeholder">
          ${t("measurements:print.chartNote")}
        </div>
      `;
    }

    // Measurements table
    html += `
      <h2>${t("measurements:measurementHistory")}</h2>
      <table>
        <thead>
          <tr>
    `;

    // Add column headers based on settings
    if (printSettings.columns.date) {
      html += `<th>${t("measurements:table.date")}</th>`;
    }
    if (printSettings.columns.weight) {
      html += `<th class="text-right">${t("measurements:table.weight")}</th>`;
    }
    if (printSettings.columns.bodyFat) {
      html += `<th class="text-right">${t("measurements:table.bodyFat")}</th>`;
    }
    if (printSettings.columns.muscleMass) {
      html += `<th class="text-right">${t("measurements:table.muscle")}</th>`;
    }
    if (printSettings.columns.visceralFat) {
      html += `<th class="text-right">${t("measurements:table.visceralFat")}</th>`;
    }
    if (printSettings.columns.bmr) {
      html += `<th class="text-right">${t("measurements:table.bmr")}</th>`;
    }

    html += `
          </tr>
        </thead>
        <tbody>
    `;

    // Add data rows
    merania.forEach(meranie => {
      html += "<tr>";
      if (printSettings.columns.date) {
        html += `<td>${formatDate(meranie.datum)}</td>`;
      }
      if (printSettings.columns.weight) {
        html += `<td class="text-right">${meranie.vahaKg || "-"}</td>`;
      }
      if (printSettings.columns.bodyFat) {
        html += `<td class="text-right">${meranie.tukPercent || "-"}</td>`;
      }
      if (printSettings.columns.muscleMass) {
        html += `<td class="text-right">${meranie.svalovinaPercent || "-"}</td>`;
      }
      if (printSettings.columns.visceralFat) {
        html += `<td class="text-right">${meranie.visceralnytuk || "-"}</td>`;
      }
      if (printSettings.columns.bmr) {
        html += `<td class="text-right">${meranie.bazalnyMetabolizmus || "-"}</td>`;
      }
      html += "</tr>";
    });

    html += `
        </tbody>
      </table>
      </body>
      </html>
    `;

    return html;
  };

  /**
   * Open print window with generated HTML
   */
  const handlePrint = (
    klient: Klient,
    merania: Meranie[],
    summary: MeasurementSummary | null,
    chartData: ChartDataPoint[]
  ) => {
    const html = generatePrintHTML(klient, merania, summary, chartData);
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  return {
    printSettings,
    setPrintSettings,
    toggleAllColumns,
    areAllColumnsSelected,
    handlePrint,
  };
}
